import { useState, useEffect } from 'react';
import { X, ArrowLeftRight, Calendar, Clock, AlertTriangle } from 'lucide-react';
import { timeOffService, ScheduleImpact } from '../../services/time-off.service';

interface RequestShiftSwapModalProps {
  agentId: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface Shift {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  channel: string;
}

interface Agent {
  id: string;
  name: string;
  team: string;
}

export default function RequestShiftSwapModal({
  agentId,
  onClose,
  onSuccess,
}: RequestShiftSwapModalProps) {
  const [myShifts, setMyShifts] = useState<Shift[]>([]);
  const [selectedMyShift, setSelectedMyShift] = useState<string>('');
  const [availableAgents, setAvailableAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>('');
  const [theirShifts, setTheirShifts] = useState<Shift[]>([]);
  const [selectedTheirShift, setSelectedTheirShift] = useState<string>('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [impact, setImpact] = useState<ScheduleImpact | null>(null);

  useEffect(() => {
    loadMyShifts();
    loadAvailableAgents();
  }, []);

  useEffect(() => {
    if (selectedAgent) {
      loadTheirShifts(selectedAgent);
    }
  }, [selectedAgent]);

  const loadMyShifts = () => {
    // Demo data - in production, fetch from API
    const demoShifts: Shift[] = [
      {
        id: 'shift-001',
        date: '2026-01-15',
        startTime: '09:00',
        endTime: '17:00',
        channel: 'Phone Support',
      },
      {
        id: 'shift-002',
        date: '2026-01-16',
        startTime: '13:00',
        endTime: '21:00',
        channel: 'Email Support',
      },
      {
        id: 'shift-003',
        date: '2026-01-17',
        startTime: '09:00',
        endTime: '17:00',
        channel: 'Phone Support',
      },
    ];
    setMyShifts(demoShifts);
  };

  const loadAvailableAgents = () => {
    // Demo data - in production, fetch from API
    const demoAgents: Agent[] = [
      { id: 'agent-002', name: 'Marcus Johnson', team: 'Technical Support' },
      { id: 'agent-003', name: 'Emily Rodriguez', team: 'Phone Support' },
      { id: 'agent-004', name: 'David Kim', team: 'Live Chat' },
      { id: 'agent-005', name: 'Jessica Taylor', team: 'Technical Support' },
    ];
    setAvailableAgents(demoAgents);
  };

  const loadTheirShifts = (_targetAgentId: string) => {
    // Demo data - in production, fetch from API based on targetAgentId
    const demoShifts: Shift[] = [
      {
        id: 'shift-101',
        date: '2026-01-14',
        startTime: '09:00',
        endTime: '17:00',
        channel: 'Phone Support',
      },
      {
        id: 'shift-102',
        date: '2026-01-15',
        startTime: '13:00',
        endTime: '21:00',
        channel: 'Live Chat',
      },
      {
        id: 'shift-103',
        date: '2026-01-18',
        startTime: '09:00',
        endTime: '17:00',
        channel: 'Email Support',
      },
    ];
    setTheirShifts(demoShifts);
  };

  const handleAnalyzeImpact = async () => {
    if (!selectedMyShift || !selectedTheirShift) return;

    try {
      const impactData = await timeOffService.analyzeShiftSwapImpact(
        selectedMyShift,
        selectedTheirShift
      );
      setImpact(impactData);
    } catch (error) {
      console.error('Failed to analyze impact:', error);
      // Use demo impact data
      setImpact({
        affectedShifts: 2,
        coverageGap: false,
        understaffedHours: 0,
        channelsAffected: [],
        skillsRequired: [],
      });
    }
  };

  const handleSubmit = async () => {
    if (!selectedMyShift || !selectedAgent || !selectedTheirShift) {
      alert('Please select all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await timeOffService.createShiftSwapRequest(agentId, {
        targetAgentId: selectedAgent,
        requestingShiftId: selectedMyShift,
        targetShiftId: selectedTheirShift,
        reason: reason || undefined,
      });
      onSuccess();
    } catch (error) {
      console.error('Failed to create shift swap request:', error);
      alert('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getShiftById = (shifts: Shift[], id: string) => {
    return shifts.find((s) => s.id === id);
  };

  const myShift = getShiftById(myShifts, selectedMyShift);
  const theirShift = getShiftById(theirShifts, selectedTheirShift);
  const targetAgent = availableAgents.find((a) => a.id === selectedAgent);

  useEffect(() => {
    if (selectedMyShift && selectedTheirShift) {
      handleAnalyzeImpact();
    } else {
      setImpact(null);
    }
  }, [selectedMyShift, selectedTheirShift]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Request Shift Swap</h2>
            <p className="text-sm text-gray-600 mt-1">
              Exchange shifts with another agent
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {/* Select My Shift */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Your Shift to Swap
            </label>
            <div className="grid grid-cols-1 gap-2">
              {myShifts.map((shift) => (
                <button
                  key={shift.id}
                  onClick={() => setSelectedMyShift(shift.id)}
                  className={`p-3 border-2 rounded-lg text-left transition-all ${
                    selectedMyShift === shift.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-gray-900">
                          {new Date(shift.date).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {shift.startTime} - {shift.endTime}
                        </span>
                        <span className="text-sm text-gray-500">• {shift.channel}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Swap Icon */}
          {selectedMyShift && (
            <div className="flex justify-center">
              <div className="p-3 bg-blue-100 rounded-full">
                <ArrowLeftRight className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          )}

          {/* Select Agent */}
          {selectedMyShift && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Agent to Swap With
              </label>
              <select
                value={selectedAgent}
                onChange={(e) => {
                  setSelectedAgent(e.target.value);
                  setSelectedTheirShift('');
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose an agent...</option>
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.name} - {agent.team}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Select Their Shift */}
          {selectedAgent && theirShifts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select {targetAgent?.name}'s Shift
              </label>
              <div className="grid grid-cols-1 gap-2">
                {theirShifts.map((shift) => (
                  <button
                    key={shift.id}
                    onClick={() => setSelectedTheirShift(shift.id)}
                    className={`p-3 border-2 rounded-lg text-left transition-all ${
                      selectedTheirShift === shift.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="font-medium text-gray-900">
                            {new Date(shift.date).toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-500" />
                          <span className="text-sm text-gray-600">
                            {shift.startTime} - {shift.endTime}
                          </span>
                          <span className="text-sm text-gray-500">• {shift.channel}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Swap Summary */}
          {myShift && theirShift && targetAgent && (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-3">Swap Summary</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600 mb-1">You give:</p>
                  <p className="font-medium">
                    {new Date(myShift.date).toLocaleDateString()} {myShift.startTime}-
                    {myShift.endTime}
                  </p>
                  <p className="text-gray-600">{myShift.channel}</p>
                </div>
                <div>
                  <p className="text-gray-600 mb-1">You receive:</p>
                  <p className="font-medium">
                    {new Date(theirShift.date).toLocaleDateString()} {theirShift.startTime}
                    -{theirShift.endTime}
                  </p>
                  <p className="text-gray-600">{theirShift.channel}</p>
                </div>
              </div>
            </div>
          )}

          {/* Impact Analysis */}
          {impact && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-green-900">Schedule Impact</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    This swap will maintain coverage with no gaps. Both agents will work the
                    same total hours.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reason for Swap (Optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Provide context for this swap request..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex items-center justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!selectedMyShift || !selectedAgent || !selectedTheirShift || isSubmitting}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Swap Request'}
          </button>
        </div>
      </div>
    </div>
  );
}
